import { isSuperUserType } from '@modules/users/utils';
import { useListTenantsQuery } from 'generated/graphql';
import { useMemo } from 'react';
import { BaseUser, UserType } from 'v2/core/entities/user';
import { MapToTenant } from 'v2/core/repository/edge-server/user/userMapper';
import { useFetchMore } from '../utils/records';

const shouldSkipTenantsCall = (userType?: UserType) => {
  if (!userType) {
    return true;
  }
  return !isSuperUserType(userType);
};

interface GetTenantListFromEdgeServerArgs {
  cursor?: string;
  user?: BaseUser;
}

const useGetTenantListFromEdgeServer = ({
  cursor,
  user,
}: GetTenantListFromEdgeServerArgs) => {
  const { id, type: userType } = user || {};

  const shouldSkip = !id || shouldSkipTenantsCall(userType);

  const {
    data,
    loading,
    error,
    fetchMore: eFetchMore,
  } = useListTenantsQuery({
    variables: { cursor },
    skip: shouldSkip,
  });

  const [isLoadingMore, fetchMore] = useFetchMore(
    eFetchMore,
    data?.listTenants?.paginationToken,
  );
  const isStatusDone = !loading && !error;

  const tenants = useMemo(
    () =>
      isStatusDone
        ? data?.listTenants?.tenants?.map((tenant) => MapToTenant(tenant))
        : [],
    [data?.listTenants, isStatusDone],
  );

  const hasMore = (() => {
    if (shouldSkip) {
      return false;
    }
    return data ? !!data.listTenants?.paginationToken : true;
  })();

  return {
    data: tenants,
    loading,
    error,
    isLoadingMore,
    fetchMore,
    hasMore,
  };
};

export default useGetTenantListFromEdgeServer;
